import { axios } from "../services";

/**
 * Cache GET en mémoire — politique par CLASSE d'endpoint, pas TTL unique.
 *
 * Pourquoi : un cache "clé=URL + TTL unique" ne sait pas exprimer qu'un
 * agrégat dépend de plusieurs entités, et en multi-poste un TTL ne voit
 * jamais les écritures des AUTRES guichets. On vise donc « converger vers
 * le frais » plutôt que « deviner une durée de validité ».
 *
 * Trois classes (par pattern d'URL, centralisé ici) :
 *  - AGRÉGÉ / dérivé (prerequisites, dashboard, recap, stats, report,
 *    suggestion) → JAMAIS caché : toujours frais. Supprime à la RACINE la
 *    classe de bug « écran périmé après une écriture faite ailleurs »
 *    (plus besoin de regex d'invalidation ni de skipCache à parsemer).
 *  - RÉFÉRENTIEL stable (countries, payment-methods, ticket-types…) →
 *    TTL long (5 min) : quasi statique.
 *  - Le reste (listes transactionnelles) → TTL court (60s).
 *
 * In-flight dedup : la même URL GET déjà en cours est partagée (TOUJOURS,
 * agrégats compris) → pas de double-fetch en rafale au mount.
 *
 * Refetch-on-focus : au retour sur l'onglet (focus / visibilitychange),
 * on purge le cache → les prochains GET repartent frais. Couvre le cas
 * multi-poste « un autre guichet a modifié la donnée pendant mon absence ».
 *
 * Bypass manuel ponctuel : `{ skipCache: true }` dans le `config` (5e arg).
 */
const TTL_REFERENTIAL_MS = 300_000; // 5 min — référentiels quasi statiques
const TTL_DEFAULT_MS = 60_000; // 60s — listes transactionnelles

// Agrégats / vues dérivées : jamais cachés (toujours frais).
const NO_CACHE_PATTERN = /\/(prerequisites|dashboard|recapitulation|recap|stats|report|suggestion)\b/i;
// Référentiels stables : TTL long.
const REFERENTIAL_PATTERN = /\/(countries|regions|cities|payment[-_]methods|ticket[-_]types|expense[-_]types|contract[-_]types|responsibility[-_]functions|features?|banks|garages|currencies)\b/i;

const isCacheable = (url: string) => !NO_CACHE_PATTERN.test(url);
const ttlFor = (url: string) => (REFERENTIAL_PATTERN.test(url) ? TTL_REFERENTIAL_MS : TTL_DEFAULT_MS);

const getCache = new Map<string, { value: any; expires: number }>();
const inflight = new Map<string, Promise<any>>();

const cacheGet = (key: string): any | undefined => {
	const entry = getCache.get(key);
	if (!entry) return undefined;
	if (entry.expires < Date.now()) {
		getCache.delete(key);
		return undefined;
	}
	return entry.value;
};
const cacheSet = (key: string, value: any) => {
	getCache.set(key, { value, expires: Date.now() + ttlFor(key) });
};

// Refetch-on-focus : converge vers le frais sans dépendre du TTL. Au
// retour de l'onglet, on vide le cache ; les GET suivants repartent au
// réseau (l'in-flight dedup évite les rafales). Enregistré une seule fois.
if (typeof window !== "undefined") {
	const purgeIfVisible = () => {
		if (typeof document === "undefined" || document.visibilityState === "visible") {
			getCache.clear();
		}
	};
	window.addEventListener("visibilitychange", purgeIfVisible);
	window.addEventListener("focus", () => getCache.clear());
}

/**
 * Invalide les entrées du cache GET dont la clé partage la ressource
 * racine avec `urlPrefix`.
 *
 * Avant ce fix, on faisait `startsWith(urlPrefix)` brut → un POST sur
 *   /api/v1/reservations/confirm/123
 * NE flush PAS le cache GET de
 *   /api/v1/reservations?station=X
 * (car la 2e clé ne commence pas par la 1ère). Résultat : après une
 * action confirm/cancel, getAll() retournait jusqu'à 60s les données
 * pré-action → impression que "rien ne s'est rafraîchi à l'écran".
 *
 * Stratégie : on extrait la ressource racine via le pattern Laravel
 * standard `/v{N}/{resource}` (cf. routes/api-v1.php) puis on
 * invalide tous les GET dont la clé partage cette racine. Pour les
 * URLs hors versionning, on retombe sur l'ancien comportement strict
 * (par sécurité — pas de flush trop large).
 */
/**
 * Dépendances croisées : mutations dont l'URL vit sous une ressource
 * racine mais qui modifient des listes servies par une AUTRE racine.
 * Ex: un scan colis (`/operation/packages/scan`) peut faire passer une
 * expédition en « arrivée » → les GET `/planning/expeditions` doivent
 * repartir frais, sinon le poste de contrôle affiche un statut périmé
 * jusqu'à 60s. Table volontairement courte : une entrée = une vraie
 * dépendance métier, pas une règle de confort.
 */
const CROSS_INVALIDATION: Array<[needle: string, roots: string[]]> = [
	["/operation/packages", ["/planning/expeditions"]],
	["/operation/expeditions", ["/planning/expeditions"]],
];

export const invalidateApiCache = (urlPrefix?: string) => {
	if (!urlPrefix) {
		getCache.clear();
		return;
	}
	for (const [needle, roots] of CROSS_INVALIDATION) {
		if (!urlPrefix.includes(needle)) continue;
		for (const r of roots) {
			for (const k of getCache.keys()) {
				if (k.includes(r)) getCache.delete(k);
			}
		}
	}
	// Capture le tronc jusqu'à la première ressource après /v{N}/.
	//   /api/v1/reservations/confirm/123 → /api/v1/reservations
	//   /api/v1/embarkations/45          → /api/v1/embarkations
	//   /api/v1/tickets                   → /api/v1/tickets
	//   /api/v2/users/42/disable          → /api/v2/users
	const m = urlPrefix.match(/^(.+\/v\d+\/[a-z][a-z0-9_-]*)/i);
	const root = m ? m[1] : urlPrefix;
	// Invalidation par ressource racine uniquement. Les agrégats ne sont
	// plus cachés (cf. NO_CACHE_PATTERN) → plus besoin de les purger ici :
	// on arrête d'empiler des règles d'invalidation.
	for (const k of getCache.keys()) {
		if (k.startsWith(root)) getCache.delete(k);
	}
};

export default (type: string, url: string, data: any, msg: string, config?: any) => {
	switch (type) {
		case "GET": {
			const fullUrl = `${url}${data}`;
			// Non caché si: bypass explicite OU endpoint agrégé/dérivé.
			// L'in-flight dedup reste actif dans tous les cas (cf. ci-dessous).
			const skipCache = config?.skipCache === true || !isCacheable(fullUrl);

			// 1) Cache hit
			if (!skipCache) {
				const cached = cacheGet(fullUrl);
				if (cached !== undefined) return Promise.resolve(cached);
			}
			// 2) In-flight dedup
			if (inflight.has(fullUrl)) {
				return inflight.get(fullUrl)!;
			}
			const promise = new Promise<any>((resolve, reject) => {
				axios
					.get(fullUrl, config)
					.then((response: any) => {
						console.log(`%c Response success when ${msg}`, "color: #5CC62C;", response);
						if (!skipCache) cacheSet(fullUrl, response.data);
						resolve(response.data);
					})
					.catch((error: any) => {
						if (error.response) {
							console.error(`Response error when ${msg}`, error.response);
							reject(error.response);
						} else if (error.request) {
							console.error(`Request error when ${msg}`, error.request);
							reject(error.request);
						} else {
							console.error(`Error when ${msg}`, error);
							reject(error);
						}
					})
					.finally(() => {
						inflight.delete(fullUrl);
					});
			});
			inflight.set(fullUrl, promise);
			return promise;
		}
		case "POST":
			return new Promise<any>((resolve, reject) => {
				// Invalide le cache GET du même prefix d'URL : sinon
				// après un POST /tickets, un GET /tickets ramène le cache
				// pré-création (jusqu'à 30s de retard pour l'utilisateur).
				invalidateApiCache(url.split('?')[0]);
				axios
					.post(`${url}`, data, config)
					.then((response: any) => {
						// handle success
						console.log(`%c Response success when ${msg}`, "color: #5CC62C;", response);
						resolve(response.data);
					})
					.catch((error: any) => {
						// handle error
						if (error.response) {
							// client received an error response (5xx, 4xx)
							console.error(`Response error when ${msg}`, error.response);
							reject(error.response);
						} else if (error.request) {
							// client never received a response, or request never left
							console.error(`Request error when ${msg}`, error.request);
							reject(error.request);
						} else {
							// anything else
							console.error(`Error when ${msg}`, error);
							reject(error);
						}
					});
			});
		case "PUT":
			return new Promise<any>((resolve, reject) => {
				invalidateApiCache(url.split('?')[0]);
				axios
					.put(`${url}`, data, config)
					.then((response: any) => {
						// handle success
						console.log(`%c Response success when ${msg}`, "color: #5CC62C;", response);
						resolve(response.data);
					})
					.catch((error: any) => {
						// handle error
						if (error.response) {
							// client received an error response (5xx, 4xx)
							console.error(`Response error when ${msg}`, error.response);
							reject(error.response);
						} else if (error.request) {
							// client never received a response, or request never left
							console.error(`Request error when ${msg}`, error.request);
							reject(error.request);
						} else {
							// anything else
							console.error(`Error when ${msg}`, error);
							reject(error);
						}
					});
			});
		case "DEL":
		case "DELETE":
			return new Promise<any>((resolve, reject) => {
				invalidateApiCache(url.split('?')[0]);
				axios
					.delete(`${url}${data}`)
					.then((response: any) => {
						// handle success
						console.log(`%c Response success when ${msg}`, "color: #5CC62C;", response);
						resolve(response.data);
					})
					.catch((error: any) => {
						// handle error
						if (error.response) {
							// client received an error response (5xx, 4xx)
							console.error(`Response error when ${msg}`, error.response);
							reject(error.response);
						} else if (error.request) {
							// client never received a response, or request never left
							console.error(`Request error when ${msg}`, error.request);
							reject(error.request);
						} else {
							// anything else
							console.error(`Error when ${msg}`, error);
							reject(error);
						}
					});
			});
		case "UPLOAD":
			return new Promise<any>((resolve, reject) => {
				invalidateApiCache(url.split('?')[0]);
				const headers =  { "Content-Type": "multipart/form-data" }
				axios
					.post(`${url}`, data, { ...config, headers })
					.then((response: any) => {
						console.log(`%c Response success when ${msg}`, "color: #5CC62C;", response);
						resolve(response.data);
					})
					.catch((error: any) => {
						if (error.response) {
							console.error(`Response error when ${msg}`, error.response);
							reject(error.response);
						} else if (error.request) {
							console.error(`Request error when ${msg}`, error.request);
							reject(error.request);
						} else {
							console.error(`Error when ${msg}`, error);
							reject(error);
						}
					});
			});
		default:
			return new Promise<any>((resolve, reject) => {
				const error = new Error("Unknown method : " + type)
				reject(error);
			});
	}
};
